import { AvailabilityOrderQL } from '@libs/trip-planner-interfaces';
import type {
  ActivitySuggestion,
  ActivitySuggestionsResponse,
  ActivitySuggestionsSearchCriteria,
  GetSuggestedAvailabilityRequestQL,
  GraphQL,
  Money,
} from '@libs/trip-planner-interfaces';
import { DateFormat, SemboDate } from '@libs/sembo-dates';

import { getApolloClient } from './infrastructure';
import { GET_SUGGESTED_QUERY } from './queries';
import { assertSmartSearchList } from './utils';
import { getRecipeQL } from './utils/requestMappers/recipeQL';

export class ActivitySuggestionService {
  public async getSuggestions(
    criteria: ActivitySuggestionsSearchCriteria
  ): Promise<ActivitySuggestionsResponse> {
    const request = adaptActivitySuggestionsSearchCriteriaToRequestQL(criteria);
    const response = await getApolloClient().query<GraphQL.GetSuggestedQuery>({
      query: GET_SUGGESTED_QUERY,
      variables: { request },
    });

    if (!response.data.getSuggestedAvailability?.results) {
      return {
        suggestions: [],
        ingredientIndex: request.ingredientIndex,
      };
    }

    assertSmartSearchList(response.data.getSuggestedAvailability.results);

    return mapToActivitySuggestions(
      response.data.getSuggestedAvailability.results,
      request.ingredientIndex
    );
  }
}

function adaptActivitySuggestionsSearchCriteriaToRequestQL(
  criteria: ActivitySuggestionsSearchCriteria
): GetSuggestedAvailabilityRequestQL {
  return {
    recipe: getRecipeQL({
      userCriteria: criteria.userCriteria.recipe,
      businessCriteria: criteria.businessCriteria,
    }),
    ingredientIndex: criteria.userCriteria.ingredientIndex,
    constraint: criteria.userCriteria.constraint,
    day: new SemboDate(criteria.userCriteria.day).format(
      DateFormat.DateReverse
    ),
    suggestedBy: AvailabilityOrderQL.Popular,
    numberOfSuggestions: 5,
  };
}

function mapToActivitySuggestions(
  response: NonNullable<
    NonNullable<
      GraphQL.GetSuggestedQuery['getSuggestedAvailability']
    >['results']
  >,
  ingredientIndex: number
): ActivitySuggestionsResponse {
  const suggestions = response.map((suggestion): ActivitySuggestion => {
    let price: Money | null = null;
    if (
      suggestion.totalPrice.amount &&
      suggestion.totalPrice.currency &&
      suggestion.totalPrice.currencySymbol
    ) {
      price = {
        amount: suggestion.totalPrice.amount ?? 0,
        currency: suggestion.totalPrice.currency,
        currencySymbol: suggestion.totalPrice.currencySymbol,
      };
    }

    return {
      code: suggestion.code,
      price,
    };
  });

  return {
    suggestions,
    ingredientIndex,
  };
}
